/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useCallback, useRef} from 'react'
import clsx from 'clsx'
import {XMarkIcon} from '@heroicons/react/24/solid'
import {useLocation} from 'react-router-dom'
import {useTypedSelector} from 'helpers/reducers'
import {useAppDispatch} from 'helpers/store'
import {closeSecondaryModal} from 'helpers/actions'
import {ModalSize} from 'types/enums/ModalSize'
import {lockBodyScroll, unlockBodyScroll} from 'utils/bodyScrollLock'

const SecondaryPopup: React.FC = () => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const prevLocationRef = useRef(`${location.pathname}${location.search}${location.hash}`)
    const secondaryModal = useTypedSelector((state) => state.ui.secondaryModal)
    const closeModalCallback = secondaryModal?.props?.closeModal

    const closeModalHandler = useCallback(() => {
        if (closeModalCallback) {
            closeModalCallback()
            return
        }
        dispatch(closeSecondaryModal())
    }, [dispatch, closeModalCallback])

    const mainModal = useTypedSelector((state) => state.ui.modal)
    const holdsBodyLockRef = React.useRef(false)
    const [hideSecondaryClose, setHideSecondaryClose] = React.useState(false)

    // Keep body scroll lock reference-counted across main + secondary modal layers.
    React.useEffect(() => {
        const shouldLock = !!mainModal?.isOpen || !!secondaryModal?.isOpen

        if (shouldLock && !holdsBodyLockRef.current) {
            lockBodyScroll()
            holdsBodyLockRef.current = true
        }

        if (!shouldLock && holdsBodyLockRef.current) {
            unlockBodyScroll()
            holdsBodyLockRef.current = false
        }
    }, [mainModal?.isOpen, secondaryModal?.isOpen])

    React.useEffect(() => {
        return () => {
            if (holdsBodyLockRef.current) {
                unlockBodyScroll()
                holdsBodyLockRef.current = false
            }
        }
    }, [])

    // detect inner close
    React.useEffect(() => {
        if (!secondaryModal?.isOpen) {
            setHideSecondaryClose(false)
            return
        }

        const t = setTimeout(() => {
            const found = document.querySelector('.ModalScrollableClose')
            setHideSecondaryClose(!!found)
        }, 20)

        return () => clearTimeout(t)
    }, [secondaryModal?.isOpen, secondaryModal?.component])

    React.useEffect(() => {
        const currentLocation = `${location.pathname}${location.search}${location.hash}`
        const didLocationChange = prevLocationRef.current !== currentLocation
        prevLocationRef.current = currentLocation

        if (!didLocationChange || !secondaryModal?.isOpen) {
            return
        }

        dispatch(closeSecondaryModal())
    }, [dispatch, location.pathname, location.search, location.hash, secondaryModal?.isOpen])

    if (!secondaryModal || !secondaryModal.component) {
        return null
    }

    const popupSize: ModalSize = (secondaryModal.props?.modalSize as ModalSize) || ModalSize.Small

    return (
        <div
            className={clsx(
                'SecondaryPopup fixed inset-0 z-[1300] flex items-center justify-center print:relative px-4',
                popupSize === ModalSize.RoundedFullscreen && 'pointer-events-none'
            )}
        >
            <div
                className={clsx(
                    'fixed inset-0',
                    popupSize === ModalSize.RoundedFullscreen
                        ? 'bg-transparent'
                        : 'bg-black/30 dark:bg-black/50'
                )}
            />
            <div
                className={clsx(
                    'relative z-40 flex flex-col',
                    popupSize === ModalSize.RoundedFullscreen && 'pointer-events-auto',
                    popupSize === ModalSize.FullScreen &&
                        'w-full h-full bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100',
                    popupSize === ModalSize.Small &&
                        'w-full max-w-md md:max-w-lg lg:max-w-3xl xl:max-w-5xl rounded-lg bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100 overflow-auto max-h-[70vh] mx-4 md:mx-8',
                    popupSize === ModalSize.Medium &&
                        'w-full max-w-[82vw] md:max-w-[82vw] rounded-lg bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100 overflow-auto max-h-[80vh] mx-4 md:mx-8',
                    popupSize === ModalSize.RoundedFullscreen &&
                        'rounded-tl-3xl border border-gray-200/80 dark:border-gray-800 bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100 overflow-auto shadow-2xl w-[calc(100vw-7rem)] h-[calc(100vh-5rem)] relative left-14 top-10',
                    popupSize === ModalSize.Scrollable &&
                        'w-full max-w-[95vw] max-h-[95vh] rounded-lg bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100 overflow-auto',
                    popupSize === ModalSize.FullHeight &&
                        'w-full max-w-4xl h-[95vh] rounded-lg bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100 overflow-auto'
                )}
            >
                {!hideSecondaryClose && (
                    <button
                        className={clsx(
                            'print:hidden sticky top-3 ml-auto mr-4 z-[100] h-10 w-10 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white transition-colors rounded-md'
                        )}
                        onClick={closeModalHandler}
                    >
                        <XMarkIcon className="w-5 h-5 text-white" />
                    </button>
                )}
                <div
                    className={clsx(
                        popupSize !== ModalSize.RoundedFullscreen && popupSize !== ModalSize.FullScreen && 'p-5',
						'h-full'
                    )}
                >
                    {(() => {
                        const comp = secondaryModal.component
                        if (React.isValidElement(comp)) {
                            return comp
                        }
                        return null
                    })()}
                </div>
            </div>
        </div>
    )
}

export default SecondaryPopup
